import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, FileText, Send, Sparkles, PauseCircle, PlayCircle, CheckCircle2, RefreshCw } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeader = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("bbc_admin_token")}` } });

const RecapEditor = ({ recap, onSaved }) => {
  const [summary, setSummary] = useState(recap.summary || "");
  const [steps, setSteps] = useState([...(recap.next_steps || []), "", "", ""].slice(0, 3));
  const [busy, setBusy] = useState("");
  const sent = recap.status === "sent";

  const call = async (label, fn) => {
    setBusy(label);
    try {
      const res = await fn();
      onSaved(res.data);
      return res.data;
    } catch (error) {
      toast.error(error.response?.data?.detail || "That did not work");
      return null;
    } finally {
      setBusy("");
    }
  };

  const save = () =>
    call("save", () =>
      axios.put(`${API}/admin/recaps/${recap.booking_id}`, { summary, next_steps: steps }, authHeader())
    ).then((data) => data && toast.success("Draft saved"));

  const send = async () => {
    const saved = await call("save", () =>
      axios.put(`${API}/admin/recaps/${recap.booking_id}`, { summary, next_steps: steps }, authHeader())
    );
    if (!saved) return;
    const result = await call("send", () =>
      axios.post(`${API}/admin/recaps/${recap.booking_id}/send`, {}, authHeader())
    );
    if (result) toast.success(`Recap sent to ${recap.client_email}`);
  };

  const regenerate = () =>
    call("regen", () =>
      axios.post(`${API}/admin/recaps/${recap.booking_id}/generate?force=true`, {}, authHeader())
    ).then((data) => {
      if (!data) return;
      setSummary(data.summary || "");
      setSteps([...(data.next_steps || []), "", "", ""].slice(0, 3));
      toast.success("Fresh draft written");
    });

  const toggleHold = () =>
    call("hold", () =>
      axios.put(`${API}/admin/recaps/${recap.booking_id}`, { hold: !recap.hold }, authHeader())
    ).then((data) => data && toast.success(data.hold ? "Auto-send paused" : "Auto-send back on"));

  return (
    <div className="border border-slate-800 bg-slate-950/60 p-5" data-testid={`recap-card-${recap.booking_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <p className="text-white text-sm">
            {recap.client_name}{" "}
            <span className="text-slate-500">· {recap.session_date} · {recap.session_type === "initial" ? "initial" : "follow-up"}</span>
          </p>
          <p className="text-slate-500 text-xs mt-1">{recap.client_email}</p>
        </div>
        {sent ? (
          <span
            className="inline-flex items-center gap-1.5 text-emerald-400 text-[10px] uppercase tracking-widest"
            data-testid={`recap-sent-badge-${recap.booking_id}`}
          >
            <CheckCircle2 size={12} />
            Sent{recap.auto_sent ? " automatically" : ""}
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-[#D4AF37] text-[10px] uppercase tracking-widest">
            <Sparkles size={12} />
            {recap.hold ? "Draft · auto-send paused" : "Draft · auto-sends after 24h"}
          </span>
        )}
      </div>

      <textarea
        value={summary}
        onChange={(e) => setSummary(e.target.value)}
        rows={4}
        disabled={sent}
        data-testid={`recap-summary-${recap.booking_id}`}
        className="w-full bg-slate-950 border border-slate-800 text-slate-200 text-sm p-3 disabled:opacity-60"
        placeholder="What the session covered"
      />

      <div className="space-y-2 mt-3">
        {steps.map((step, index) => (
          <div key={index} className="flex items-center gap-3">
            <span className="text-[#D4AF37] font-serif text-sm w-4">{index + 1}</span>
            <input
              value={step}
              onChange={(e) => setSteps(steps.map((s, i) => (i === index ? e.target.value : s)))}
              disabled={sent}
              data-testid={`recap-step-${index}-${recap.booking_id}`}
              className="flex-1 bg-slate-950 border border-slate-800 text-slate-200 text-sm px-3 h-10 disabled:opacity-60"
              placeholder={`Next step ${index + 1}`}
            />
          </div>
        ))}
      </div>

      {!sent && (
        <div className="flex flex-wrap gap-2 mt-4">
          <button
            onClick={send}
            disabled={!!busy || !summary.trim()}
            data-testid={`recap-send-btn-${recap.booking_id}`}
            className="bg-[#D4AF37] text-slate-950 px-5 h-10 text-xs uppercase tracking-widest hover:bg-[#B5952F] disabled:opacity-40 transition-colors inline-flex items-center gap-2"
          >
            {busy === "send" ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
            Approve & send
          </button>
          <button
            onClick={save}
            disabled={!!busy}
            data-testid={`recap-save-btn-${recap.booking_id}`}
            className="border border-slate-700 text-slate-300 px-5 h-10 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors"
          >
            Save draft
          </button>
          <button
            onClick={regenerate}
            disabled={!!busy}
            data-testid={`recap-regen-btn-${recap.booking_id}`}
            className="border border-slate-700 text-slate-400 px-5 h-10 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2"
          >
            {busy === "regen" ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
            Rewrite
          </button>
          <button
            onClick={toggleHold}
            disabled={!!busy}
            data-testid={`recap-hold-btn-${recap.booking_id}`}
            className="border border-slate-700 text-slate-400 px-5 h-10 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2"
          >
            {recap.hold ? <PlayCircle size={13} /> : <PauseCircle size={13} />}
            {recap.hold ? "Resume auto-send" : "Hold"}
          </button>
        </div>
      )}
    </div>
  );
};

export const AdminRecapsTab = () => {
  const [recaps, setRecaps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sweeping, setSweeping] = useState(false);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/admin/recaps`, authHeader());
      setRecaps(res.data);
    } catch {
      setRecaps([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const sweep = async () => {
    setSweeping(true);
    try {
      const res = await axios.post(`${API}/admin/recaps/sweep`, {}, authHeader());
      const { drafted, auto_sent: autoSent } = res.data;
      toast.success(`${drafted} draft(s) written, ${autoSent} sent automatically`);
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Sweep failed");
    } finally {
      setSweeping(false);
    }
  };

  const replace = (updated) =>
    setRecaps((current) => current.map((r) => (r.booking_id === updated.booking_id ? updated : r)));

  const drafts = recaps.filter((r) => r.status !== "sent");
  const sent = recaps.filter((r) => r.status === "sent");

  return (
    <div className="bg-slate-900/50 border border-slate-800 p-6" data-testid="admin-recaps-tab">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <h2 className="font-serif text-xl text-white mb-2 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#D4AF37]" />
            Session recaps
          </h2>
          <p className="text-slate-400 text-sm max-w-2xl">
            An hour after each completed session the AI drafts the client's recap and three next steps from their
            intake, incident log and your prep brief. Edit it, press send — or leave it and it goes out on its own
            after 24 hours with their vulnerability report attached.
          </p>
        </div>
        <button
          onClick={sweep}
          disabled={sweeping}
          data-testid="recap-sweep-btn"
          className="border border-slate-700 text-slate-300 px-5 h-11 text-xs uppercase tracking-widest hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors inline-flex items-center gap-2"
        >
          {sweeping ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          Draft now
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="animate-spin text-[#D4AF37]" size={22} />
        </div>
      ) : recaps.length === 0 ? (
        <div className="border border-dashed border-slate-700 p-10 text-center" data-testid="recaps-empty">
          <p className="text-slate-400 text-sm">
            No recaps yet — the first one appears an hour after a session is completed.
          </p>
        </div>
      ) : (
        <div className="space-y-8">
          {drafts.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">
                Waiting for you ({drafts.length})
              </p>
              <div className="space-y-4" data-testid="recap-drafts">
                {drafts.map((recap) => (
                  <RecapEditor key={recap.booking_id} recap={recap} onSaved={replace} />
                ))}
              </div>
            </div>
          )}
          {sent.length > 0 && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-slate-500 mb-3">Sent ({sent.length})</p>
              <div className="space-y-4" data-testid="recap-sent">
                {sent.map((recap) => (
                  <RecapEditor key={recap.booking_id} recap={recap} onSaved={replace} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
